import * as Yup from 'yup'
import { dateFormats } from 'constants/common'
import parse from 'date-fns/parse'
import format from 'date-fns/format'
import _isEmpty from 'lodash/isEmpty'
import { isDateValid } from 'utils/common'
import {
  bookingPageRoute,
  BookingPageType,
  BOOKING_SECTIONS,
} from '../constants'
import { FILTER_KEY_MAPPING } from 'app/ProviderSearch/constants'
import { CounselingTypeEnum } from 'app/Bookings/constants'
import { differenceInYears, isMatch } from 'date-fns'

export const baseDateValidation = Yup.string()
  .required('Date of birth is required')
  .test('validDate', 'Please enter a valid date', (value) => {
    if (!value) {
      return false
    }

    const parsedDate = parse(
      value,
      dateFormats.YEAR_MONTH_DAY_FORMAT,
      new Date()
    )
    return !isNaN(parsedDate.getTime())
  })
  .test('futureDate', 'Date cannot be in the future', (value) => {
    if (!value) {
      return false
    }

    const parsedDate = parse(
      value,
      dateFormats.YEAR_MONTH_DAY_FORMAT,
      new Date()
    )
    return parsedDate <= new Date()
  })
  .test('maxAgeLimit', 'Age cannot be more than 110 years', (value) => {
    if (!value) {
      return false
    }

    const parsedDate = parse(
      value,
      dateFormats.YEAR_MONTH_DAY_FORMAT,
      new Date()
    )

    if (isNaN(parsedDate.getTime())) {
      return false
    }

    const age = differenceInYears(new Date(), parsedDate)
    return age <= 110
  })

export const adultDateValidation = baseDateValidation.test(
  'minimumAge',
  'Patient must be at least 18 years old',
  (value) => {
    if (!value) {
      return false
    }

    const parsedDate = parse(
      value,
      dateFormats.YEAR_MONTH_DAY_FORMAT,
      new Date()
    )

    if (isNaN(parsedDate.getTime())) {
      return false
    }

    const age = differenceInYears(new Date(), parsedDate)
    return age >= 18
  }
)

export const childDateValidation = baseDateValidation.test(
  'maxAge',
  'Patient must be under 18 years old',
  (value) => {
    if (!value) {
      return false
    }

    const parsedDate = parse(
      value,
      dateFormats.YEAR_MONTH_DAY_FORMAT,
      new Date()
    )

    if (isNaN(parsedDate.getTime())) {
      return false
    }

    const age = differenceInYears(new Date(), parsedDate)
    return age < 18
  }
)

export const formatDateForDobInput = (
  value: string,
  convertFromDashedToDisplayFormat: boolean
): string => {
  const inputFormat = convertFromDashedToDisplayFormat
    ? dateFormats.YEAR_MONTH_DAY_FORMAT
    : dateFormats.MONTH_DAY_YEAR_DOB_FORMAT
  const outputFormat = convertFromDashedToDisplayFormat
    ? dateFormats.MONTH_DAY_YEAR_DOB_FORMAT
    : dateFormats.YEAR_MONTH_DAY_FORMAT

  // If value is already in output format, return as is
  if (isMatch(value, outputFormat)) {
    return value
  }

  if (isDateValid(value)) {
    const parsedDate = parse(value, inputFormat, new Date())
    return format(parsedDate, outputFormat)
  }

  return ''
}

// Base validation schema for cardHolderName
export const baseSchema = Yup.object().shape({
  cardHolderName: Yup.string()
    .required('Cardholder name is required')
    .test('no-leading-spaces', 'Cardholder name is required', (value) => {
      if (!value) {
        return true
      }

      return value === value.trimStart()
    }),
})

// Address validation schema
export const addressSchema = Yup.object().shape({
  line1: Yup.string().trim().required('Street Address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string()
    .required('State is required')
    .min(2, 'Must be 2 characters')
    .max(2, 'Must be 2 characters')
    .matches(/^[a-zA-Z]+$/, 'Must be only letters'),
  zipcode: Yup.string()
    .required('ZIP code is required')
    .matches(/^[0-9]+$/, 'Must be only digits')
    .min(5, 'Must be exactly 5 digits')
    .max(5, 'Must be exactly 5 digits'),
})

// Function to create validation schema with address validation
export const withAddress = (): Yup.AnyObjectSchema =>
  baseSchema.shape({
    address: addressSchema,
  })

// Date input mask logic
type MaskedValue = {
  value: string
  selection: {
    start: number
    end: number
    length: number
  } | null
}

const stringRepeat = (letter: string, length: number): string => {
  return letter.repeat(length)
}

const replaceMaskWithLetter = (
  value: string,
  letter: string,
  length: number
): string => {
  if (!value) {
    value = ''
  }

  value = value.replace(/[^0-9]/g, '')

  return value + stringRepeat(letter, length - value.length)
}

const fillInMaskWithLetters = (value: string): string => {
  if (!value) {
    return ''
  }

  const [month, day, year] = value.split('/')

  return [
    replaceMaskWithLetter(month, 'M', 2),
    replaceMaskWithLetter(day, 'D', 2),
    replaceMaskWithLetter(year, 'Y', 4),
  ].join('/')
}

const findFirstPlaceholderIndex = (value: string): number => {
  const placeholderPositions = [
    value.indexOf('M'),
    value.indexOf('D'),
    value.indexOf('Y'),
  ].filter((position) => position >= 0)

  if (placeholderPositions.length === 0) {
    return null
  }

  return Math.min(...placeholderPositions)
}

export const beforeMaskedValueChange = (newState: MaskedValue): MaskedValue => {
  const value = fillInMaskWithLetters(newState.value)
  let selection = newState.selection

  if (selection && selection.length === 1) {
    const index =
      findFirstPlaceholderIndex(value) || Math.max(0, value.length - 1)

    selection = {
      start: index,
      end: index,
      length: 1,
    }
  }

  return { value, selection }
}

export const checkUniquePatient = (
  allPatients: PatientEntity[],
  newPatient: PatientEntity
): boolean => {
  const isDuplicate = allPatients.some(
    (patient) =>
      patient.id !== newPatient.id &&
      patient.firstName.toLowerCase() === newPatient.firstName.toLowerCase() &&
      patient.lastName.toLowerCase() === newPatient.lastName.toLowerCase() &&
      patient.dateOfBirth === newPatient.dateOfBirth
  )
  return isDuplicate
}

export const getClientDetailsRouteFromCounselingTypeId = (
  counselingTypeIdParam: string,
  globalGroupedFilters: GroupedProviderFilter
): string => {
  const selectedCounselingType = globalGroupedFilters[
    FILTER_KEY_MAPPING.counselingType
  ]
    .filter((filter) => filter.type === FILTER_KEY_MAPPING.counselingType)
    .find((filter) => filter.id === counselingTypeIdParam)

  switch (selectedCounselingType?.value) {
    case CounselingTypeEnum.INDIVIDUAL:
      return `${bookingPageRoute.CLIENT_DETAILS}/individual`
    case CounselingTypeEnum.MEDICATION:
      return `${bookingPageRoute.CLIENT_DETAILS}/med-psy`
    case CounselingTypeEnum.COUPLES:
      return `${bookingPageRoute.CLIENT_DETAILS}/couples`
    case CounselingTypeEnum.FAMILY:
      return `${bookingPageRoute.CLIENT_DETAILS}/family`
    default:
      return bookingPageRoute.CLIENT_DETAILS
  }
}

export const isMinor = (dateOfBirth: string): boolean => {
  if (!dateOfBirth) {
    return true
  }

  const parsedDate = parse(
    dateOfBirth,
    dateFormats.YEAR_MONTH_DAY_FORMAT,
    new Date()
  )

  if (isNaN(parsedDate.getTime())) {
    return true
  }

  const age = differenceInYears(new Date(), parsedDate)
  return age < 18
}

/**
 * Derives the current/active booking section from the pathname.
 * Route is the source of truth (no "current section" in state) to stay in sync with App Router and browser history.
 */
export function getCurrentSectionFromPath(
  pathname: string
): BookingPageType | null {
  for (const section of BOOKING_SECTIONS) {
    if (pathname.includes(bookingPageRoute[section])) {
      return section
    }
  }

  return null
}
